import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = [
  'System QoS - Energy Groups',
  'System QoS - ReMoni Accounts',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      systemQosSystemQosAccountIdEnergyGroupIdGet: build.query<
        SystemQosSystemQosAccountIdEnergyGroupIdGetApiResponse,
        SystemQosSystemQosAccountIdEnergyGroupIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/system_qos/${queryArg.accountId}/${queryArg.energyGroupId}`,
        }),
        providesTags: ['System QoS - Energy Groups'],
      }),
      allSystemQosAllSystemQosGet: build.query<
        AllSystemQosAllSystemQosGetApiResponse,
        AllSystemQosAllSystemQosGetApiArg
      >({
        query: () => ({ url: `/all_system_qos` }),
        providesTags: ['System QoS - Energy Groups'],
      }),
      resetSystemQosResetSystemQosAccountIdEnergyGroupIdPut: build.mutation<
        ResetSystemQosResetSystemQosAccountIdEnergyGroupIdPutApiResponse,
        ResetSystemQosResetSystemQosAccountIdEnergyGroupIdPutApiArg
      >({
        query: (queryArg) => ({
          url: `/reset_system_qos/${queryArg.accountId}/${queryArg.energyGroupId}`,
          method: 'PUT',
        }),
        invalidatesTags: ['System QoS - Energy Groups'],
      }),
      genericSystemQosGenericSystemQosAccountIdGet: build.query<
        GenericSystemQosGenericSystemQosAccountIdGetApiResponse,
        GenericSystemQosGenericSystemQosAccountIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/generic_system_qos/${queryArg.accountId}`,
        }),
        providesTags: ['System QoS - ReMoni Accounts'],
      }),
      allGenericSystemQosAllGenericSystemQosGet: build.query<
        AllGenericSystemQosAllGenericSystemQosGetApiResponse,
        AllGenericSystemQosAllGenericSystemQosGetApiArg
      >({
        query: () => ({ url: `/all_generic_system_qos` }),
        providesTags: ['System QoS - ReMoni Accounts'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type SystemQosSystemQosAccountIdEnergyGroupIdGetApiResponse =
  /** status 200 Successful Response */ SystemQoSOutput;
export type SystemQosSystemQosAccountIdEnergyGroupIdGetApiArg = {
  /** The energy group id to get the system QoS for. */
  energyGroupId: number;
  /** The account id to get the system QoS for. */
  accountId: number;
};
export type AllSystemQosAllSystemQosGetApiResponse =
  /** status 200 Successful Response */ SummarizedSystemQoSOutput[];
export type AllSystemQosAllSystemQosGetApiArg = void;
export type ResetSystemQosResetSystemQosAccountIdEnergyGroupIdPutApiResponse =
  /** status 200 Successful Response */ SystemQoSOutput;
export type ResetSystemQosResetSystemQosAccountIdEnergyGroupIdPutApiArg = {
  /** The energy group id to reset the system QoS for. */
  energyGroupId: number;
  /** The account id to reset the system QoS for. */
  accountId: number;
};
export type GenericSystemQosGenericSystemQosAccountIdGetApiResponse =
  /** status 200 Successful Response */ GenericSystemQoSOutput;
export type GenericSystemQosGenericSystemQosAccountIdGetApiArg = {
  /** The account id to get the system QoS for. */
  accountId: number;
};
export type AllGenericSystemQosAllGenericSystemQosGetApiResponse =
  /** status 200 Successful Response */ GenericSummarizedSystemQoSOutput[];
export type AllGenericSystemQosAllGenericSystemQosGetApiArg = void;
export type HealthCheckWarning = {
  /** Warning message */
  message: string;
  /** Type of warning */
  warning_type: string;
  /** List of device IDs */
  device_ids?: (number | string)[];
  /** Account ID */
  account_id?: number;
  /** Subscription ID */
  subscription_id?: number;
  /** Building ID */
  building_id?: number;
};
export type HealthCheckError = {
  /** Warning message */
  message: string;
  /** Type of error */
  error_type: string;
  /** List of device IDs */
  device_ids?: (number | string)[];
  /** Account ID */
  account_id?: number;
  /** Subscription ID */
  subscription_id?: number;
  /** Building ID */
  building_id?: number;
};
export type DagsterHealthCheckModel = {
  /** Name of the Health Check */
  Name?: string;
  /** Device IDs of the Health Check */
  DeviceIds?: (number | string)[];
  /** Status of the Health Check */
  Status?: string;
  /** List of warnings */
  Warnings?: HealthCheckWarning[];
  /** List of errors */
  Errors?: HealthCheckError[];
};
export type SystemQoSData = {
  /** The gateway data. */
  GatewayData?: object[];
  /** The sensor data. */
  SensorData?: object[];
  /** The units data health check data. */
  DataHealthCheckData?: object[];
  /** The installation check data. */
  InstallationCheckData?: object[];
};
export type DagsterWarning = {
  /** Warning message */
  message: string;
  /** Operator where the warning occurred */
  op: string;
  /** Type of warning */
  warning_type: string;
  /** Account ID */
  account_id?: number;
  /** Subscription ID */
  subscription_id?: number;
  /** Building ID */
  building_id?: number;
};
export type DagsterError = {
  /** Warning message */
  message: string;
  /** Operator where the error occurred */
  op: string;
  /** Type of error */
  error_type: string;
  /** Account ID */
  account_id?: number;
  /** Subscription ID */
  subscription_id?: number;
  /** Building ID */
  building_id?: number;
};
export type SystemQoSOutput = {
  /** The energy group id. */
  EnergyGroupId: number;
  /** The account id. */
  AccountId: number;
  /** The execution time of the health check. Format: YYYY-MM-DDTHH:MM:SSZ */
  ExecutionTime: string;
  /** The system quality of service score. */
  SystemQoSScore: number;
  /** The overall status of the system. Possible values: 'Success', 'Warning', 'Error', 'Critical' */
  OverallStatus: string;
  /** The health checks performed. */
  HealthChecks: DagsterHealthCheckModel[];
  /** Underlying data for different entities such as gateways, sensors and units. */
  Data?: SystemQoSData;
  /** The warnings generated during processing. */
  DagsterWarnings?: DagsterWarning[];
  /** The errors generated during processing. */
  DagsterErrors?: DagsterError[];
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type SummarizedSystemQoSOutput = {
  /** The energy group id. */
  EnergyGroupId: number;
  /** The account id. */
  AccountId: number;
  /** The execution time of the health check. Format: YYYY-MM-DDTHH:MM:SSZ */
  ExecutionTime: string;
  /** The system quality of service score. */
  SystemQoSScore: number;
  /** The overall status of the system. Possible values: 'Success', 'Warning', 'Error', 'Critical' */
  OverallStatus: string;
  /** The health checks performed. */
  HealthChecks: DagsterHealthCheckModel[];
  /** The warnings generated during processing. */
  DagsterWarnings?: DagsterWarning[];
  /** The errors generated during processing. */
  DagsterErrors?: DagsterError[];
};
export type GenericSystemQoSData = {
  /** The gateway data. */
  GatewayData?: object[];
  /** The sensor data. */
  SensorData?: object[];
  /** The units data health check data. */
  DataHealthCheckData?: object[];
};
export type GenericSystemQoSOutput = {
  /** The account id. */
  AccountId: number;
  /** The execution time of the health check. Format: YYYY-MM-DDTHH:MM:SSZ */
  ExecutionTime: string;
  /** The system quality of service score. */
  SystemQoSScore: number;
  /** The overall status of the system. Possible values: 'Success', 'Warning', 'Error', 'Critical' */
  OverallStatus: string;
  /** The health checks performed. */
  HealthChecks: DagsterHealthCheckModel[];
  /** Underlying data for different entities such as gateways, sensors and units. */
  Data?: GenericSystemQoSData;
  /** The warnings generated during processing. */
  DagsterWarnings?: DagsterWarning[];
  /** The errors generated during processing. */
  DagsterErrors?: DagsterError[];
};
export type GenericSummarizedSystemQoSOutput = {
  /** The account name. */
  Name: string;
  /** The account id. */
  AccountId: number;
  /** The execution time of the health check. Format: YYYY-MM-DDTHH:MM:SSZ */
  ExecutionTime: string;
  /** The system quality of service score. */
  SystemQoSScore: number;
  /** The overall status of the system. Possible values: 'Success', 'Warning', 'Error', 'Critical' */
  OverallStatus: string;
  /** The health checks performed. */
  HealthChecks: DagsterHealthCheckModel[];
  /** The warnings generated during processing. */
  DagsterWarnings?: DagsterWarning[];
  /** The errors generated during processing. */
  DagsterErrors?: DagsterError[];
};
export const {
  useSystemQosSystemQosAccountIdEnergyGroupIdGetQuery,
  useAllSystemQosAllSystemQosGetQuery,
  useResetSystemQosResetSystemQosAccountIdEnergyGroupIdPutMutation,
  useGenericSystemQosGenericSystemQosAccountIdGetQuery,
  useAllGenericSystemQosAllGenericSystemQosGetQuery,
} = injectedRtkApi;
